/**
 * 261030 Phase B3 / B3-5 — tab «Nhân vật» của bước ②: MỘT nhân vật MỘT thẻ, bên trong là các hàng (hình thái/tuổi × bộ đồ). Con vật nằm cùng tab (bỏ tab «Con vật»).
 * CHỈ logic thuần (không React / antd) để jest chạy được. Dữ liệu: `assets.characterGroups` (BE) + thẻ ở `assets.tabs.{characters,animals}` (khoá `cardKey` = `key` của thẻ).
 */
import type { AssetCard, AssetsStatus } from './workflowFourAssetsUtils';

export interface AssetGroupForm {
  /** id nhân vật của hình thái (gốc = chính nhân vật; bản tuổi / hình thái = nhân vật biến thể). */
  characterId: string;
  isBase: boolean;
  /** `null` ⇔ gốc. Hình thái FORM (người sói…) không có dòng tuổi. */
  variantKind?: 'AGE' | 'FORM' | null;
  /** «6 tuổi» · «Bản chính» · mô tả hình thái («người sói»). */
  label: string;
  ageYears: number | null;
  rank: number;
  /** `key` của các thẻ (mỗi bộ đồ một thẻ) trong `assets.tabs.*`. */
  looks: string[];
}
/** Cờ theo hàng do BE dựng sẵn (`characterGroups[*].flagsByCard`, hợp đồng B3 Rev 1 §2). Không cờ nào chặn `canApprove` cổng 2. */
export interface BeRowFlag {
  code: 'OUTFIT_UNSOURCED' | 'AGE_UNKNOWN' | 'AGE_MISMATCH' | string;
  severity: 'info' | 'warn';
  text: string;
  scenes?: Array<{ sceneId: string; heading: string | null; orderIndex: number | null }>;
  action?: { kind: 'REGENERATE'; cardKey: string } | null;
}
export interface AssetCharacterGroup {
  characterId: string;
  name: string;
  kind: 'PERSON' | 'ANIMAL';
  forms: AssetGroupForm[];
  flagsByCard?: Record<string, BeRowFlag[]>;
}

export type AssetRowFlagTone = 'info' | 'warn' | 'bad';
export interface AssetRowFlag {
  code: string;
  tone: AssetRowFlagTone;
  text: string;
  /** Cờ có hành động «Tạo lại» ⇒ nút «Tạo lại» của hàng nổi bật (AGE_MISMATCH). */
  regen: boolean;
}
/** Cờ BE → cờ hiển thị. `info` ⇒ dòng mờ một dòng (không nút); `warn` ⇒ khung cảnh báo + «Tạo lại» nổi bật khi BE gửi action. Chữ do BE dựng; thiếu chữ thì rơi về bảng mã. */
const FLAG_FALLBACK: Record<string, string> = { OUTFIT_UNSOURCED: 'Giữ bộ chính — truyện không tả bộ khác', AGE_UNKNOWN: 'chưa rõ tuổi', AGE_MISMATCH: 'Tuổi trong ảnh có vẻ lệch so với truyện' };
export function rowFlagsOf(flags: BeRowFlag[] | null | undefined): AssetRowFlag[] {
  return (flags ?? []).map((f) => ({ code: f.code, tone: f.severity === 'warn' ? 'warn' : 'info', text: (f.text && f.text.trim()) || FLAG_FALLBACK[f.code] || f.code, regen: f.action?.kind === 'REGENERATE' }));
}

export interface AssetRow {
  key: string;
  card: AssetCard;
  form: AssetGroupForm;
  /** «6 tuổi · bộ chính» / «8 tuổi · áo choàng xanh». */
  label: string;
  /** Số video (đúng `eventOrder`) dùng hàng này, tăng dần. */
  videos: number[];
  /** Số cảnh (`scene.orderIndex`) có video dùng hàng này, tăng dần — chỉ khi màn có bảng event → cảnh; không có ⇒ []. */
  scenes: number[];
  /** Bộ đồ này CÓ được video nào của plan hiện tại dùng không (`card.eventIds` do BE trả; không suy theo tên). */
  used: boolean;
  /** Dòng mờ dự phòng («chưa rõ tuổi» khi chưa có cờ BE). */
  notes: string[];
  flags: AssetRowFlag[];
}
export interface AssetGroupView {
  characterId: string;
  name: string;
  kind: 'PERSON' | 'ANIMAL';
  rows: AssetRow[];
  /** Bộ đồ DÙNG trong plan (hiện trước, dải chính). Nhân vật chưa có bộ nào dùng ⇒ giữ NGUYÊN mọi hàng ở đây (không có gì để tách, không giấu thẻ cần vẽ). */
  mainRows: AssetRow[];
  /** Bộ đồ KHÔNG dùng trong plan này (chỉ khi nhân vật có ít nhất một bộ dùng) — gấp thành «Bộ đồ khác của nhân vật · N», không tính vào «cần vẽ» của plan. */
  otherRows: AssetRow[];
  /** Ảnh đại diện của thẻ: ảnh hàng bộ chính của hình thái gốc, rơi về ảnh đầu tiên có. */
  imageUrl: string | null;
  /** Con vật: loài / mô tả ngắn (đầu thẻ). */
  species: string | null;
  ready: number;
  total: number;
  attention: 'none' | 'running' | 'bad';
}

/** Thẻ của tab «Nhân vật» = nhân vật + con vật (cùng tab, B3-4). */
export function characterTabCardsOf(assets: Pick<AssetsStatus, 'tabs'> | null | undefined): AssetCard[] {
  return [...((assets?.tabs?.characters ?? []) as AssetCard[]), ...((assets?.tabs?.animals ?? []) as AssetCard[])];
}

/** `lookKey` → chữ đọc được: `default`/rỗng ⇒ «bộ chính»; `scene_look_mist` / `mistCloak` ⇒ «scene look mist» / «mist cloak». */
export function lookLabelOf(lookKey: string | null | undefined, isMain?: boolean): string {
  const raw = String(lookKey ?? '').trim();
  if (!raw || raw === 'default' || (isMain && !raw)) return 'bộ chính';
  const text = raw.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_\-.]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  return text || 'bộ chính';
}

/** Hàng «N tuổi · bộ chính». Con vật không có bộ đồ ⇒ chỉ hình thái. */
export function rowLabelOf(form: Pick<AssetGroupForm, 'label'>, card: Pick<AssetCard, 'lookKey' | 'isMain' | 'kind'>): string {
  const main = !card.lookKey || card.lookKey === 'default';
  if (card.kind === 'ANIMAL' && main) return form.label;
  return `${form.label} · ${lookLabelOf(card.lookKey, card.isMain)}`;
}

/** «8 tuổi» / «age 8» / «8-year-old» ⇒ 8; không có ⇒ null (không đoán). Chỉ dùng ở nhóm dự phòng. */
export function ageFromLabel(label: string | null | undefined): number | null {
  const m = /(\d{1,3})\s*(?:tuổi|-?\s*year)/i.exec(label ?? '') ?? /\bage\s*(\d{1,3})/i.exec(label ?? '');
  return m ? Number(m[1]) : null;
}

/** Nhóm dự phòng khi BE chưa gửi `characterGroups` (status cũ): gom theo nhân vật gốc từ chính các thẻ. */
export function groupsFromCardsOf(assets: Pick<AssetsStatus, 'tabs'> | null | undefined): AssetCharacterGroup[] {
  const byBase = new Map<string, AssetCharacterGroup>();
  for (const card of characterTabCardsOf(assets)) {
    const charId = card.characterId || card.ownerId || card.key;
    const baseId = card.variantOf?.characterId || charId;
    let group = byBase.get(baseId);
    if (!group) { group = { characterId: baseId, name: card.variantOf?.name || card.name, kind: card.kind === 'ANIMAL' ? 'ANIMAL' : 'PERSON', forms: [] }; byBase.set(baseId, group); }
    if (!card.variantOf) { group.name = card.name; group.kind = card.kind === 'ANIMAL' ? 'ANIMAL' : group.kind; }
    let form = group.forms.find((f) => f.characterId === charId);
    if (!form) {
      const label = card.ageOrForm || card.variantOf?.description || 'Bản chính';
      form = { characterId: charId, isBase: !card.variantOf, label, ageYears: card.variantOf?.kind === 'FORM' ? null : ageFromLabel(label), rank: group.forms.length, looks: [] };
      group.forms.push(form);
    }
    form.looks.push(card.key);
  }
  const list = Array.from(byBase.values());
  for (const g of list) g.forms.sort((a, b) => Number(b.isBase) - Number(a.isBase) || a.rank - b.rank);
  return list;
}

export const groupsOf = (assets: (Pick<AssetsStatus, 'tabs'> & { characterGroups?: AssetCharacterGroup[] | null }) | null | undefined): AssetCharacterGroup[] =>
  assets?.characterGroups && assets.characterGroups.length ? assets.characterGroups : groupsFromCardsOf(assets);

const isBad = (c: AssetCard): boolean => c.status === 'FAILED' || c.status === 'BLOCKED' || c.status === 'UNCERTAIN' || (!!c.kyc?.required && (c.kyc.status === 'FAILED' || c.kyc.status === 'UNKNOWN'));

/**
 * Dữ liệu vẽ tab «Nhân vật». Cờ theo hàng lấy từ `group.flagsByCard[cardKey]` (BE dựng sẵn). Không có `flagsByCard` (nhóm dự phòng) thì FE chỉ nói «chưa rõ tuổi» khi thiếu tuổi.
 * Thẻ không thuộc nhóm nào (BE chưa kịp gom) vẫn hiện — mỗi thẻ một nhóm lẻ — để không có ảnh nào biến mất khỏi màn.
 */
export function groupViewsOf(
  assets: (Pick<AssetsStatus, 'tabs'> & { characterGroups?: AssetCharacterGroup[] | null }) | null | undefined,
  eventOrderOf: (eventId: string) => number | null,
  eventSceneOf?: (eventId: string) => number | null,
): AssetGroupView[] {
  const cards = new Map(characterTabCardsOf(assets).map((c) => [c.key, c]));
  const used = new Set<string>();
  const views: AssetGroupView[] = [];
  for (const group of groupsOf(assets)) {
    const rows: AssetRow[] = [];
    const forms = group.forms.slice().sort((a, b) => Number(b.isBase) - Number(a.isBase) || a.rank - b.rank);
    for (const form of forms) {
      for (const key of form.looks) {
        const card = cards.get(key);
        if (!card || used.has(key)) continue;
        used.add(key);
        const flags = rowFlagsOf(group.flagsByCard?.[key]);
        const notes: string[] = [];
        const isFormKind = (form.variantKind ?? card.variantOf?.kind) === 'FORM';
        // Nhóm dự phòng (BE chưa gửi cờ): hàng bộ chính của người thiếu tuổi vẫn nói «chưa rõ tuổi», không đoán.
        if (!group.flagsByCard && group.kind === 'PERSON' && !isFormKind && card.isMain && form.ageYears == null) notes.push('chưa rõ tuổi');
        rows.push({ key, card, form, label: rowLabelOf(form, card), videos: videosOf(card.eventIds, eventOrderOf), scenes: scenesOf(card.eventIds, eventSceneOf), used: isUsed(card), notes, flags });
      }
    }
    if (!rows.length) continue;
    views.push(viewOf(group.characterId, group.name, group.kind, rows));
  }
  // Thẻ chưa thuộc nhóm nào: gom theo nhân vật của thẻ (mỗi nhân vật một nhóm lẻ).
  const orphans = new Map<string, AssetRow[]>();
  for (const [key, card] of Array.from(cards.entries())) {
    if (used.has(key)) continue;
    const owner = card.characterId || key;
    const form: AssetGroupForm = { characterId: owner, isBase: true, label: card.ageOrForm || 'Bản chính', ageYears: null, rank: 0, looks: [key] };
    const list = orphans.get(owner) ?? [];
    list.push({ key, card, form, label: rowLabelOf(form, card), videos: videosOf(card.eventIds, eventOrderOf), scenes: scenesOf(card.eventIds, eventSceneOf), used: isUsed(card), notes: [], flags: [] });
    orphans.set(owner, list);
  }
  for (const [owner, rows] of Array.from(orphans.entries())) views.push(viewOf(owner, rows[0].card.name, rows[0].card.kind === 'ANIMAL' ? 'ANIMAL' : 'PERSON', rows));
  return views;
}
function viewOf(characterId: string, name: string, kind: 'PERSON' | 'ANIMAL', rows: AssetRow[]): AssetGroupView {
  const { mainRows, otherRows } = splitRowsByUseOf(rows);
  // «cần vẽ» / «ảnh xong» chỉ tính các bộ ở dải chính; bộ không dùng trong plan không làm đổi số này.
  const cardsOfRows = mainRows.map((r) => r.card);
  const main = rows.find((r) => r.form.isBase && r.card.isMain && r.card.imageUrl) ?? rows.find((r) => r.card.imageUrl);
  return {
    characterId, name, kind, rows, mainRows, otherRows, imageUrl: main?.card.imageUrl ?? null, species: kind === 'ANIMAL' ? (rows.find((r) => r.card.species)?.card.species ?? null) : null,
    ready: cardsOfRows.filter((c) => c.status === 'READY').length, total: cardsOfRows.length,
    // attention xét MỌI hàng: ảnh lỗi ở bộ không dùng vẫn phải có chỗ nhìn thấy.
    attention: rows.some((r) => isBad(r.card) || r.flags.some((f) => f.tone === 'warn')) ? 'bad' : cardsOfRows.some((c) => c.status === 'RUNNING' || c.kyc?.status === 'PROCESSING') ? 'running' : 'none',
  };
}

/** Số video (eventOrder) từ danh sách id; id không biết thứ tự bị bỏ; không trùng, tăng dần. */
export function videosOf(eventIds: string[] | null | undefined, eventOrderOf: (eventId: string) => number | null): number[] {
  const out = new Set<number>();
  for (const id of eventIds ?? []) { const n = eventOrderOf(id); if (typeof n === 'number' && Number.isFinite(n)) out.add(n); }
  return Array.from(out).sort((a, b) => a - b);
}

export const VIDEO_LIST_FOLD_AFTER = 4;
/** Dòng «dùng trong …» của hàng: 0 video ⇒ «chưa dùng trong video nào»; > 4 video ⇒ gấp (hiện 4 đầu + «và N video nữa»). */
export function videoLineOf(videos: number[], expanded = false): { text: string; foldable: boolean; hidden: number } {
  if (!videos.length) return { text: 'chưa dùng trong video nào', foldable: false, hidden: 0 };
  const foldable = videos.length > VIDEO_LIST_FOLD_AFTER;
  const shown = foldable && !expanded ? videos.slice(0, VIDEO_LIST_FOLD_AFTER) : videos;
  const hidden = foldable && !expanded ? videos.length - shown.length : 0;
  return { text: `dùng trong video ${shown.join(', ')}${hidden ? ` và ${hidden} video nữa` : ''}`, foldable, hidden };
}

/** Bộ đồ này được ít nhất một video (event) của plan dùng — theo `card.eventIds` BE trả. */
const isUsed = (card: Pick<AssetCard, 'eventIds'>): boolean => (card.eventIds?.length ?? 0) > 0;

/** Tách bộ đồ DÙNG / KHÔNG DÙNG trong plan. Không bộ nào dùng (hoặc BE chưa gửi `eventIds`) ⇒ không tách: mọi hàng ở dải chính. */
export function splitRowsByUseOf<R extends Pick<AssetRow, 'used'>>(rows: ReadonlyArray<R>): { mainRows: R[]; otherRows: R[] } {
  if (!rows.some((r) => r.used)) return { mainRows: rows.slice(), otherRows: [] };
  return { mainRows: rows.filter((r) => r.used), otherRows: rows.filter((r) => !r.used) };
}

/** Số cảnh từ danh sách id event; id không biết cảnh bị bỏ; không trùng, tăng dần. */
export function scenesOf(eventIds: string[] | null | undefined, eventSceneOf?: (eventId: string) => number | null): number[] {
  if (!eventSceneOf) return [];
  const out = new Set<number>();
  for (const id of eventIds ?? []) { const n = eventSceneOf(id); if (typeof n === 'number' && Number.isFinite(n)) out.add(n); }
  return Array.from(out).sort((a, b) => a - b);
}

/**
 * Dòng «dùng ở: Cảnh 2, 4 · 6 video» của một bộ đồ. Chưa biết cảnh (màn chưa có bảng event → cảnh) ⇒ null (dòng «dùng trong video …» có sẵn lo phần video).
 * Bộ không dùng ⇒ null (dòng «chưa dùng trong video nào» có sẵn lo phần đó).
 */
export function sceneUsageLineOf(row: Pick<AssetRow, 'used' | 'scenes' | 'videos'>): string | null {
  if (!row.used || !row.scenes.length) return null;
  const videos = row.videos.length ? ` · ${row.videos.length} video` : '';
  return `dùng ở: Cảnh ${row.scenes.join(', ')}${videos}`;
}

/** Nhãn dòng gấp «Bộ đồ khác của nhân vật · N». */
export const otherLooksLabelOf = (n: number): string => `Bộ đồ khác của nhân vật · ${n}`;
